import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../api/axiosInstance';
import { toast } from 'sonner';

// ─── Reusable thunk factory ───────────────────────────────────────────────────
export const createApiThunk = (name, fn) =>
  createAsyncThunk(name, async (arg, { rejectWithValue }) => {
    try {
      const data = await fn(arg);
      return data;
    } catch (err) {
      const msg = err.response?.data?.message || err.message || `${name} failed`;
      toast.error(msg);
      return rejectWithValue(msg);
    }
  });

// ─── parentId normalize karo ─────────────────────────────────────────────────
// Sirf 0, '', undefined, null → null
// Koi bhi valid number (1, 2, 5, 100...) waise hi rehne do
const normalizeCategory = (cat) => ({
  ...cat,
  parentId: cat.parentId === 0 || cat.parentId === '' || cat.parentId === undefined || cat.parentId === null ? null : Number(cat.parentId) // string ID ho to number ban jaye
});

// ─── Category Thunks ──────────────────────────────────────────────────────────
export const fetchCategories = createApiThunk('category/fetchAll', async () => {
  const { data } = await api.get('Categories');
  return data.data;
});

export const createCategory = createApiThunk('category/create', async (dto) => {
  const { data } = await api.post('Categories', dto);
  toast.success('Category created successfully');
  // API response mein parentId missing/wrong ho sakta hai
  // isliye jo dto bheja tha uska parentId forcefully merge karo
  return { ...data.data, parentId: dto.parentId ?? null };
});

export const updateCategory = createApiThunk('category/update', async ({ id, dto }) => {
  const { data } = await api.put(`Categories/${id}`, dto);
  toast.success('Category updated');
  // Same fix: dto ka parentId reliable hai, response ka nahi
  return { ...data.data, parentId: dto.parentId ?? null };
});

export const toggleCategoryStatus = createApiThunk('category/toggleStatus', async ({ id, active }) => {
  await api.patch(`Categories/${id}/status?active=${active}`);
  toast.success(`Category ${active ? 'activated' : 'deactivated'}`);
  return { id, active };
});

// ─── Slice ────────────────────────────────────────────────────────────────────
const categorySlice = createSlice({
  name: 'category',
  initialState: { items: [], loading: false, error: null },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCategories.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      // fetch: poori list normalize karo
      .addCase(fetchCategories.fulfilled, (s, a) => {
        s.loading = false;
        s.items = (a.payload || []).map(normalizeCategory);
      })
      .addCase(fetchCategories.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload;
      })

      // create: naya item normalize karke add karo
      .addCase(createCategory.fulfilled, (s, a) => {
        const newItem = normalizeCategory(a.payload);
        s.items = [newItem, ...s.items];
      })

      // update: updated item normalize karke replace karo
      .addCase(updateCategory.fulfilled, (s, a) => {
        const updated = normalizeCategory(a.payload);
        s.items = s.items.map((i) => (i.categoryId === updated.categoryId ? updated : i));
      })

      .addCase(toggleCategoryStatus.fulfilled, (s, a) => {
        s.items = s.items.map((i) => (i.categoryId === a.payload.id ? { ...i, isActive: a.payload.active } : i));
      });
  }
});

export default categorySlice.reducer;
